import { Grid, makeStyles, Typography } from '@material-ui/core';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CategoryIcon from '@material-ui/icons/Category';
import { Content, InfoCard, Link, Page } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  HomePageStarredEntities,
  HomePageToolkit,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { DevQuote } from '@parsifal-m/plugin-dev-quotes-homepage';
import { XkcdComicCard } from 'backstage-plugin-xkcd';
import { GithubIncidentSign } from './GithubIncidentSign';
import { LatestNewsCard } from './LatestNewsCard';

const HELPDESK_URL =
  'https://taskbox.karcher.com/plugins/servlet/desk/portal/97/create';
// Fits 5 news rows (~60px each, allowing one wrapped title) plus header and padding.
const NEWS_ROW_HEIGHT = 420;
// xkcd aspect ratios vary wildly; without a cap a tall comic stretches the whole card.
const COMIC_MAX_HEIGHT = 340;

const useStyles = makeStyles(theme => ({
  searchBarInput: {
    maxWidth: '60vw',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '50px',
    boxShadow: theme.shadows[1],
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
  // Fixed height, not maxHeight: the two cards must match whatever they contain, and the
  // card is the grid item's only child so `& > *` hits it without generated class names.
  cappedCard: {
    '& > *': {
      height: NEWS_ROW_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
    },
    '& [class*="MuiCardContent-root"]': {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
    },
  },
  // HomePageToolkit exposes no sizing props. Substring selectors because Backstage's nested
  // MUI v4 theme suffixes generated class names (MuiCardContent-root-123).
  toolkit: {
    '& > [class*="MuiCard-root"]': {
      height: '100%',
    },
    '& [class*="MuiCardContent-root"], & [class*="MuiCardContent-root"]:last-child':
      {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      },
    '& [class*="MuiList-root"]': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& [class*="MuiListItemIcon-root"]': {
      width: 40,
      height: 40,
    },
  },
  // XkcdComicCard brings its own InfoCard; flatten it so the pair reads as a single card.
  comic: {
    '& [class*="MuiPaper-root"]': {
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
    '& img': {
      maxHeight: COMIC_MAX_HEIGHT,
    },
  },
  // Two documentation links, so InfoCard's single `deepLink` is not enough; this mirrors
  // the BottomLink look it would have rendered. The Backstage theme defaults every
  // MuiCardActions to justifyContent: flex-end, hence the explicit override.
  docsActions: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 2.5),
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  docsLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    // Cast: MUI v4's FontWeightProperty predates the csstype union the theme now uses.
    fontWeight: theme.typography.fontWeightBold as number,
  },
}));

const tools = [
  { url: '/catalog', label: 'Catalog', icon: <CategoryIcon /> },
  { url: '/create', label: 'Create...', icon: <CreateComponentIcon /> },
];

export const HomePage = () => {
  const classes = useStyles();
  const smartbox = useApi(configApiRef).getConfig('felix.smartbox');
  // new URL tolerates a stray trailing slash on baseUrl or a missing leading one on the path.
  const smartboxPage = (page: string) =>
    new URL(
      smartbox.getString(`pages.${page}`),
      smartbox.getString('baseUrl'),
    ).toString();

  const requestPortalUrl = smartboxPage('requestPortal');
  const getStartedUrl = smartboxPage('getStarted');
  const kdpDocsUrl = smartboxPage('kdpDocs');
  const kapDocsUrl = smartboxPage('kapDocs');
  const whatIsKapUrl = smartboxPage('whatIsKap');

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <Grid container justifyContent="center" spacing={3}>
            <Grid container item xs={12} justifyContent="center">
              <HomePageSearchBar
                InputProps={{
                  classes: {
                    root: classes.searchBarInput,
                    notchedOutline: classes.searchBarOutline,
                  },
                }}
                placeholder="Search"
              />
            </Grid>
            <Grid item xs={12}>
              <InfoCard
                title="Kärcher Developer Platform & Kärcher Application Platform"
                subheader="Everything you need to build, ship and run software at Kärcher — KDP for the toolchain, KAP for the runtime"
                actionsClassName={classes.docsActions}
                actions={
                  <>
                    <Link
                      to={kdpDocsUrl}
                      underline="none"
                      className={classes.docsLink}
                    >
                      Read the full KDP documentation in Smartbox
                      <ArrowForwardIcon fontSize="small" />
                    </Link>
                    <Link
                      to={kapDocsUrl}
                      underline="none"
                      className={classes.docsLink}
                    >
                      Read the full KAP documentation in Smartbox
                      <ArrowForwardIcon fontSize="small" />
                    </Link>
                  </>
                }
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Typography variant="body1" paragraph>
                      KDP brings source control, CI/CD, deployment, artifacts,
                      and security together, so you spend less time wiring up
                      tooling and more time on your product. It runs on AWS and
                      Kubernetes and follows GitOps principles, with a DevSecOps
                      mindset.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      GitHub for code, collaboration and AI assistance · GitHub
                      Actions on self-hosted runners and Jenkins for CI/CD ·
                      ArgoCD for GitOps deployments to Kubernetes · Nexus and
                      GitHub Packages for artifacts · GitHub Advanced Security
                      and Renovate to keep you secure and up to date · Terraform
                      modules and Helm charts as ready-made building blocks.
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What you build lands on the{' '}
                      <strong>Kärcher Application Platform (KAP)</strong> —
                      pre-configured AWS accounts, production-ready EKS clusters
                      with the add-ons already wired up, New Relic observability
                      and a security baseline. KAP runs the platform foundation;
                      you keep your application and its own AWS resources.{' '}
                      <Link to={whatIsKapUrl}>What is KAP</Link> walks through
                      that split in detail.
                    </Typography>
                    <Typography variant="body2">
                      New here? The{' '}
                      <Link to={getStartedUrl}>KDP Get Started guide</Link>{' '}
                      takes you from access to a running, secured and observable
                      service.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" gutterBottom>
                      How to reach us
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Questions and discussion</strong> — the KDP & KAP
                      Engineering Teams Google Chat spaces.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Support and consulting</strong> —{' '}
                      <Link to={`${HELPDESK_URL}/4030`}>
                        raise a request with the KDP & KAP Engineering Teams
                      </Link>
                      .
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Something broken?</strong>{' '}
                      <Link to={`${HELPDESK_URL}/4019`}>
                        Report a platform incident
                      </Link>
                      .
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Access and permissions</strong> —{' '}
                      <Link to={`${HELPDESK_URL}/2916`}>
                        request user or application permissions
                      </Link>
                      .
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Not sure which one fits? The{' '}
                      <Link to={requestPortalUrl}>Request Portal guide</Link>{' '}
                      explains all three request types and what each form needs.
                    </Typography>
                  </Grid>
                </Grid>
              </InfoCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <GithubIncidentSign />
            </Grid>
            <Grid item xs={12} md={6} className={classes.toolkit}>
              <HomePageToolkit tools={tools} />
            </Grid>
            <Grid item xs={12} md={6} className={classes.cappedCard}>
              <LatestNewsCard />
            </Grid>
            <Grid item xs={12} md={6} className={classes.cappedCard}>
              <HomePageStarredEntities />
            </Grid>
            <Grid item xs={12}>
              <InfoCard
                title="Your Daily Dose of Humor"
                subheader="A comic and a quote while the build runs"
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6} className={classes.comic}>
                    <XkcdComicCard />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DevQuote />
                  </Grid>
                </Grid>
              </InfoCard>
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
